<template>
  <v-tooltip
    :disabled="tooltipDisabled"
    bottom>
    <template v-slot:activator="{ on, attrs }">
      <v-autocomplete
        ref="autocomplete"
        outlined
        v-bind="mergeAttrs($attrs, attrs)"
        v-on="mergeListeners($listeners, on)"
        :value="value"
        :append-icon="hideIcon ? '' : 'mdi-chevron-down'"
        :color="defaultColor"
        :persistent-placeholder="persistentPlaceholder"
        :class="inputClass"
        :style="inputStyle"
        @input="$emit('input', $event)">
        <template v-if="$scopedSlots.selection" v-slot:selection="slotProps">
          <slot name="selection" v-bind="slotProps" />
        </template>

        <template v-else-if="hasChips" v-slot:selection="slotProps">
          <v-tooltip
            bottom
            :color="defaultColor"
            :disabled="isSelectionTooltipDisabled(slotProps)">
            <template v-slot:activator="{ on: tooltipChipListeners, attrs: tooltipChipAttrs }">
              <v-chip
                class="chip-autocomplete-content"
                v-bind="mergeAttrs(slotProps.attrs, tooltipChipAttrs)"
                color="primary"
                :input-value="slotProps.selected"
                :disabled="slotProps.disabled"
                :close="hasDeletableChips && !slotProps.disabled"
                :small="hasSmallChips"
                @click="slotProps.select"
                @click:close="onChipClose(slotProps)"
                v-on="tooltipChipListeners">
                <span class="chip-autocomplete-line">
                  {{ getSelectionText(slotProps) }}
                </span>
              </v-chip>
            </template>
            <span>
              {{ getSelectionText(slotProps) }}
            </span>
          </v-tooltip>
        </template>

        <template v-if="$scopedSlots.item" v-slot:item="slotProps">
          <slot name="item" v-bind="slotProps" />
        </template>

        <template v-if="$scopedSlots['prepend-item']" v-slot:prepend-item>
          <slot name="prepend-item" />
        </template>

        <template v-if="$scopedSlots['append-item']" v-slot:append-item>
          <slot name="append-item" />
        </template>

        <template v-if="$scopedSlots['no-data']" v-slot:no-data>
          <slot name="no-data" />
        </template>
      </v-autocomplete>
    </template>
    <span>
      {{ tooltipText }}
    </span>
  </v-tooltip>
</template>

<script>
import Formatters from '@/shared/formatters/formatters';

export default {
  name: 'Autocomplete',

  inheritAttrs: false,

  props: {
    value: [String, Number, Boolean, Object, Array],

    regular: {
      type: Boolean,
      default: false,
    },

    defaultColor: {
      type: String,
      default: 'primary',
    },

    textColor: {
      type: String,
      default: '',
    },

    persistentPlaceholder: {
      type: Boolean,
      default: true,
    },

    borderSelected: {
      type: Boolean,
      default: false,
    },

    hideIcon: {
      type: Boolean,
      default: false,
    },

    showTooltip: {
      type: Boolean,
      default: false,
    },

    tooltipValue: {
      default: false,
    },
  },

  computed: {
    mainClass() {
      return {
        'autocomplete-default': true,
        'autocomplete-border-selected': this.hasValue && this.borderSelected,
        'autocomplete-persist-placeholder': this.persistentPlaceholder,
        'autocomplete-regular': this.regular,
        'autocomplete-text-color': this.textColor !== '',
      };
    },

    inputClass() {
      const attrs = this.$attrs;
      const data = this.$vnode && this.$vnode.data;
      const classes = [];
      if (data) {
        if (data.staticClass) classes.push(data.staticClass);
        if (data.class) classes.push(data.class);
      }
      return [
        this.mainClass,
        attrs.class,
        ...classes,
      ];
    },

    inputStyle() {
      const attrs = this.$attrs;
      const data = this.$vnode && this.$vnode.data;
      const styles = [];
      if (data) {
        if (data.staticStyle) styles.push(data.staticStyle);
        if (data.style) styles.push(data.style);
      }
      if (this.textColor !== '') {
        styles.push({ '--autocomplete-text-color': `var(--v-${this.textColor}-base)` });
      }
      return [
        attrs.style,
        ...styles,
      ];
    },

    hasValue() {
      const formatter = new Formatters();

      if (Array.isArray(this.value) && this.value.length > 0) return true;
      if (Array.isArray(this.value) && this.value.length === 0) return false;
      if (formatter.isEmptyValue(this.value)) return false;
      return true;
    },

    tooltipSourceValue() {
      return this.tooltipValue !== false ? this.tooltipValue : this.value;
    },

    tooltipText() {
      return this.formatTooltipValue(this.tooltipSourceValue);
    },

    tooltipDisabled() {
      return !this.showTooltip || this.isTooltipEmpty(this.tooltipSourceValue);
    },

    hasChips() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'chips') && this.$attrs.chips !== false;
    },

    hasSmallChips() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'small-chips') && this.$attrs['small-chips'] !== false;
    },

    hasDeletableChips() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'deletable-chips') && this.$attrs['deletable-chips'] !== false;
    },
  },

  methods: {
    focus() {
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.focus();
      }
    },

    openMenu() {
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.isMenuActive = true;
      }
    },

    mergeListeners(listeners, tooltipOn) {
      const merged = Object.assign({}, listeners);
      if (!tooltipOn) return merged;
      Object.keys(tooltipOn).forEach((event) => {
        const current = merged[event];
        if (current) {
          merged[event] = Array.isArray(current) ? current.concat(tooltipOn[event]) : [current, tooltipOn[event]];
          return;
        }
        merged[event] = tooltipOn[event];
      });
      return merged;
    },

    mergeAttrs(attrs, tooltipAttrs) {
      return Object.assign({}, attrs, tooltipAttrs);
    },

    formatTooltipValue(value) {
      if (Array.isArray(value)) {
        return value
          .map((item) => this.formatSingleTooltipValue(item))
          .filter((item) => item !== '')
          .join(', ');
      }
      return this.formatSingleTooltipValue(value);
    },

    formatSingleTooltipValue(value) {
      if (this.isTooltipEmpty(value)) return '';
      if (value && typeof value === 'object') {
        return this.formatPrimitiveTooltipValue(this.getItemText(value));
      }
      const itemFromList = this.findItemFromItems(value);
      if (itemFromList) {
        return this.formatPrimitiveTooltipValue(this.getItemText(itemFromList));
      }
      return this.formatPrimitiveTooltipValue(value);
    },

    formatPrimitiveTooltipValue(value) {
      if (this.isTooltipEmpty(value)) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (value && typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch (error) {
          return String(value);
        }
      }
      return String(value);
    },

    getItemText(item) {
      const itemText = this.$attrs['item-text'] || this.$attrs.itemText;
      if (typeof itemText === 'function') {
        return itemText(item);
      }
      if (itemText && item && typeof item === 'object' && itemText in item) {
        return item[itemText];
      }
      if (item && typeof item === 'object') {
        if ('text' in item) return item.text;
        if ('name' in item) return item.name;
        if ('label' in item) return item.label;
        if ('description' in item) return item.description;
      }
      return item;
    },

    getItemValue(item) {
      const itemValue = this.$attrs['item-value'] || this.$attrs.itemValue;
      if (typeof itemValue === 'function') {
        return itemValue(item);
      }
      if (itemValue && item && typeof item === 'object' && itemValue in item) {
        return item[itemValue];
      }
      if (item && typeof item === 'object') {
        if ('value' in item) return item.value;
        if ('id' in item) return item.id;
      }
      return item;
    },

    findItemFromItems(value) {
      const items = this.$attrs.items;
      if (!Array.isArray(items)) return null;
      return items.find((item) => {
        if (item && typeof item === 'object') {
          return this.getItemValue(item) === value;
        }
        return item === value;
      });
    },

    isTooltipEmpty(value) {
      const formatter = new Formatters();
      if (Array.isArray(value)) return value.length === 0;
      if (value && typeof value === 'object') return Object.keys(value).length === 0;
      return formatter.isEmptyValue(value);
    },

    getSelectionItem(slotProps) {
      if (slotProps && typeof slotProps === 'object' && 'item' in slotProps) return slotProps.item;
      return slotProps;
    },

    getSelectionText(slotProps) {
      return this.formatSingleTooltipValue(this.getSelectionItem(slotProps));
    },

    isSelectionTooltipDisabled(slotProps) {
      return this.isTooltipEmpty(this.getSelectionText(slotProps));
    },

    onChipClose(slotProps) {
      if (!slotProps || !slotProps.parent) return;
      const item = this.getSelectionItem(slotProps);
      if (typeof slotProps.parent.onChipInput === 'function') {
        slotProps.parent.onChipInput(item);
        return;
      }
      if (typeof slotProps.parent.selectItem === 'function') {
        slotProps.parent.selectItem(item);
      }
    },

    getInputElement() {
      return this.$refs.autocomplete?.$el || null;
    },

    getInputWidth() {
      const el = this.getInputElement();
      if (!el) return 0;
      return Math.round(el.getBoundingClientRect().width);
    },
  },
};
</script>

<style lang="scss">
.autocomplete-default :is([class*="v-input__prepend"], [class*="v-input__append"]) {
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
}

.autocomplete-default.v-text-field--full-width .v-input__prepend-outer,
.autocomplete-default.v-text-field--full-width .v-input__prepend-inner,
.autocomplete-default.v-text-field--full-width .v-input__append-inner,
.autocomplete-default.v-text-field--full-width .v-input__append-outer,
.autocomplete-default.v-text-field--enclosed .v-input__prepend-outer,
.autocomplete-default.v-text-field--enclosed .v-input__prepend-inner,
.autocomplete-default.v-text-field--enclosed .v-input__append-inner,
.autocomplete-default.v-text-field--enclosed .v-input__append-outer {
  margin-top: 0 !important;
}

.autocomplete-default:not(:is([class*="field--disabled"])):is([class*="v-input--is-disabled"])
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: color-mix(in srgb, var(--v-border-base) 40%, transparent) !important;
}

.autocomplete-default:not(:is([class*="field--disabled"])):is([class*="v-input--is-disabled"])
  > .v-input__control
    > .v-input__slot {
      label,
      input,
      i {
        color: color-mix(in srgb, var(--v-foreground-base) 40%, transparent) !important;
      }
}

.autocomplete-default:not(.autocomplete-border-selected):not(.v-input--is-focused):not(.v-input--has-state):not(:is([class*="field--disabled"], [class*="v-input--is-disabled"]))
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: var(--v-border-base) !important;
}

.autocomplete-border-selected:not(.v-input--is-focused):not(.v-input--has-state)
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: var(--v-primary-base) !important;
}

.autocomplete-default fieldset {
  border-width: 2px;
}

.autocomplete-default .v-text-field__details {
  margin-bottom: 5px !important;
}

.autocomplete-default {
  > .v-input__control {
    ::before {
      border-image: none !important;
    }

    > .v-input__slot {
      padding: 0 12px !important;
      min-height: 50px;
      margin-bottom: 1px;

      > .v-text-field__slot {
        margin: 0 !important;
        height: 100%;
      }

      &::before,
      &::after,
      :before,
      :after {
        border-color: transparent !important;
        background-color: transparent !important;
      }

      label {
        padding: 0 8px;
        font-size: 16px;
        color: var(--v-foreground-base);
        max-width: 133%;
        line-height: 1;
      }
    }
  }
}

.autocomplete-regular {
  margin: 0px !important;
  padding: 0px !important;

  > .v-input__control {
    > .v-input__slot {
      margin-bottom: 1px;
    }
  }
}

.autocomplete-persist-placeholder {
  > .v-input__control {
    > .v-input__slot {
      label {
        background-color: transparent;
        transform: translate(-6px, -26px) scale(0.75);
        pointer-events: auto;
      }
    }
  }
}

.chip-autocomplete-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
}

.chip-autocomplete-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.autocomplete-text-color input {
  color: var(--autocomplete-text-color) !important;
}
</style>
